import { track } from 'src/lib/analytics/utils';
import { waitAFrame } from '../../lib/util/waitAFrame';
import type { Component, Entity2D } from '../../odie';

import type { PhaseId } from '../defs/types';
import { GameScene } from '../GameScene';
import { captureGenericError } from 'src/lib/sentry';

/*
    game phase state
*/
export class PhaseComponent implements Component {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'phase';

  // fields
  //-------------------------------------------------------------------------
  // injected
  public entity!: Entity2D;

  // state
  private _phase!: PhaseId;
  private _lastPhase!: PhaseId;
  // game rounds
  private _round!: number;
  // current activation count. if over 0 then phase is active.
  private _activations!: number;
  // moves count
  private _moves!: number;
  // total moves counter
  private _moveCounter!: number;
  // if a resolved collision was player initiated
  private _moved!: boolean;

  private activationsPromise?: Promise<void>;
  private activationsResolve?: () => void;

  // properties
  //-------------------------------------------------------------------------
  public get phase(): PhaseId {
    return this._phase;
  }

  public set phase(phase: PhaseId) {
    // if changing
    if (phase !== this._phase) {
      this._lastPhase = this._phase;
      this._phase = phase;
      this.entity.setModified(PhaseComponent, { phase: true });
    }
  }

  public get round(): number {
    return this._round;
  }

  public get lastPhase(): PhaseId {
    return this._lastPhase;
  }

  public get activations(): number {
    return this._activations;
  }

  public get moved(): boolean {
    return this._moved;
  }

  public set moved(moved: boolean) {
    this._moved = moved;
  }

  public get moves(): number {
    return this._moves;
  }

  public set moves(moves: number) {
    this._moves = moves;
  }

  public get moveCounter(): number {
    return this._moveCounter;
  }

  // impl
  //-------------------------------------------------------------------------
  public init(): void {
    this._phase = 'none';
    this._lastPhase = 'none';
    this._round = 0;
    this._activations = 0;
    // true allows for initial round event
    this._moves = 0;
    this._moveCounter = 0;
    this._moved = true;
  }

  // api
  //-------------------------------------------------------------------------
  public activePush() {
    // increment activations
    ++this._activations;
  }

  public async activePop() {
    // if not in active phase, wait a frame to trigger it
    if (this._phase !== 'active') await waitAFrame();

    // decrement activations
    --this._activations;

    // Report to sentry if somehow activations got negative
    // Only report if it's -1 to prevent spamming this too much in case that happens
    if (this._activations === -1) {
      captureGenericError('PuzzleError', new Error('Activations got negative'));
    }

    if (this._activations <= 0) {
      // Resolve the activations promise once all activations are popped
      this.resolveWaitForActivations();
    }
  }

  public triggerMove() {
    // set moved state
    this.moved = true;

    // decrement moves
    --this._moves;

    // increment move counter
    ++this._moveCounter;

    // Might be destroyed already
    if (!this.entity.scene) return;

    // notify
    (this.entity.scene as GameScene).events.publish({ id: 'move' });
  }

  public triggerRound() {
    // reset moved
    this.moved = false;

    // increment round
    ++this._round;

    this.entity.setModified(PhaseComponent, { round: true });

    // Might be destroyed already
    if (!this.entity.scene) return;

    // notify round
    (this.entity.scene as GameScene).events.publish({ id: 'round' });
  }

  /**
   * Wait for all activations to be popped (become 0)
   */
  public async waitActivationsComplete() {
    if (this._activations <= 0) return;

    if (!this.activationsPromise) {
      this.activationsPromise = new Promise<void>((resolve) => {
        this.activationsResolve = resolve;
      });
    }

    await this.activationsPromise;
  }

  /**
   * Resolve the activations promise if it exists
   */
  private resolveWaitForActivations() {
    this.activationsResolve?.();
    this.activationsPromise = undefined;
    this.activationsResolve = undefined;
  }
}
